import React, { useState, useEffect } from "react";
import {
  X,
  Loader2,
  Mail,
  Users,
  AlertCircle,
  ChevronRight,
  Search,
  Plus
} from "lucide-react";
import { fetchMailingLists } from "../../services/MailingListService";

/**
 * MailingListPickerModal - Select a mailing list for sending postcards
 *
 * Used by PropertyPostcardBuilderModal to choose where to send designed postcards.
 */
const MailingListPickerModal = ({ isOpen, onClose, onSelect, onCreateNew }) => {
  const [lists, setLists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Load mailing lists
  useEffect(() => {
    if (!isOpen) return;

    const loadLists = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await fetchMailingLists();
        setLists(data.lists || []);
      } catch (err) {
        setError(err.message || "Failed to load mailing lists");
      } finally {
        setIsLoading(false);
      }
    };

    loadLists();
  }, [isOpen]);

  // Filter lists by search
  const filteredLists = lists.filter(list =>
    list.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    list.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[140] flex items-center justify-center bg-slate-900/50 dark:bg-slate-950/80 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-lg shadow-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold dark:text-white text-slate-900">
                  Select Mailing List
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Choose where to send your postcards
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search */}
          <div className="mt-4 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search mailing lists..."
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {/* Loading */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-sky-600 animate-spin" />
            </div>
          )}

          {/* Error */}
          {error && !isLoading && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !error && filteredLists.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
                <Mail className="w-8 h-8 text-slate-400" />
              </div>
              <h4 className="font-semibold dark:text-white text-slate-900 mb-2">
                {searchQuery ? "No lists found" : "No Mailing Lists"}
              </h4>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                {searchQuery
                  ? "Try a different search term"
                  : "Create a mailing list first to send postcards"}
              </p>
              {onCreateNew && !searchQuery && (
                <button
                  onClick={onCreateNew}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg font-medium transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Create Mailing List
                </button>
              )}
            </div>
          )}

          {/* Lists */}
          {!isLoading && !error && filteredLists.length > 0 && (
            <div className="space-y-2">
              {filteredLists.map((list) => (
                <button
                  key={list.id || list.list_id}
                  onClick={() => onSelect(list)}
                  className="w-full p-4 border border-slate-200 dark:border-slate-700 rounded-xl text-left hover:border-sky-300 dark:hover:border-sky-700 hover:bg-sky-50 dark:hover:bg-sky-900/20 transition-all group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold dark:text-white text-slate-900 truncate">
                        {list.name || list.list_name}
                      </h4>
                      {list.description && (
                        <p className="text-sm text-slate-500 dark:text-slate-400 truncate mt-0.5">
                          {list.description}
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-sm text-slate-500 dark:text-slate-400">
                        <span className="flex items-center gap-1">
                          <Users className="w-3.5 h-3.5" />
                          {(list.contact_count || list.contactCount || 0).toLocaleString()} contacts
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-sky-600 dark:group-hover:text-sky-400 transition-colors flex-shrink-0" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default MailingListPickerModal;
